import { useMemo } from 'react';

import { SignalReferenceContext } from './signal-reference-context.js';

import type { HilSignal } from '@cadstrom/backend/shared';
import type { ReactNode } from 'react';
import type { SignalReferenceContextValue } from './signal-reference-context.js';

export interface SignalReferenceContextProviderProps {
	/** The signals that `:signal[…]` references in the subtree resolve against. */
	signals: readonly HilSignal[];
	children: ReactNode;
}

/**
 * Provides the signals that `:signal[…]` markdown references in the subtree resolve against.
 * Duplicate names resolve to the first occurrence, so when several packages' signals are
 * concatenated the caller's stable ordering decides which one a bare name means.
 */
export function SignalReferenceContextProvider({ signals, children }: SignalReferenceContextProviderProps) {
	const value = useMemo((): SignalReferenceContextValue => {
		// Map#set is last-wins on duplicate keys; guard so the first occurrence wins instead.
		const signalsByName = new Map<string, HilSignal>();
		for (const signal of signals) {
			if (!signalsByName.has(signal.name)) {
				signalsByName.set(signal.name, signal);
			}
		}
		return { signalsByName, allSignals: [...signals] };
	}, [signals]);

	return <SignalReferenceContext value={value}>{children}</SignalReferenceContext>;
}
